import React, { useState } from "react";
import { capture } from "../../analytics/posthog";
import { quizLeadEmail } from "../../features/start/answers";
import { rememberEmailLinkReturn } from "./emailLinkReturn";
import { postRequest } from "../../services/apiService";

interface EmailLinkFormProps {
  /** Where the form is rendered. Rides on analytics events. */
  source: string;
}

const inQuiz = () => window.location.pathname.startsWith("/start");

/**
 * "Email me a sign-in link", for people who won't use Google. Posts to
 * /api/v1/auth/email/request. The link lands on /auth/email, which finishes the
 * sign-in. The answer never reveals whether an account exists for the address.
 * From the quiz it starts with the address they already gave and returns to their plan.
 */
const EmailLinkForm: React.FC<EmailLinkFormProps> = ({ source }) => {
  const [email, setEmail] = useState(() => (inQuiz() ? quizLeadEmail() : ""));
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || status === "sending") return;
    setStatus("sending");
    capture("magic_link_requested", { source });
    rememberEmailLinkReturn();
    const resp = await postRequest("/api/v1/auth/email/request", {
      email: trimmed,
      next: inQuiz() ? "/start/plan" : undefined,
    });
    setStatus(resp?.ok ? "sent" : "error");
  };

  if (status === "sent") {
    return (
      <p role="status" style={{ color: "#94c2dc", fontSize: 14, textAlign: "center", margin: "12px 0 0" }}>
        Check your inbox. The sign-in link works once and expires in 15 minutes.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <label style={{ flex: "1 1 200px", minWidth: 0, margin: 0 }}>
          <span className="sr-only">Email address</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Or use your email"
            autoComplete="email"
            required
            style={{
              width: "100%",
              background: "rgba(1,24,58,0.6)",
              border: "1px solid rgba(151,197,223,0.3)",
              borderRadius: 6,
              color: "#fff",
              padding: "10px 12px",
              fontSize: 16,
            }}
          />
        </label>
        <button
          type="submit"
          disabled={status === "sending"}
          style={{
            flex: "1 0 auto",
            background: "#94c2dc",
            color: "#01183a",
            border: 0,
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 13,
            padding: "10px 14px",
            cursor: status === "sending" ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {status === "sending" ? "Sending…" : "Email me a link"}
        </button>
      </div>
      {status === "error" && (
        <p role="alert" style={{ color: "#f3a6a6", fontSize: 13, margin: "8px 0 0" }}>
          Couldn't send a link to that address. Check the email and try again in a minute.
        </p>
      )}
    </form>
  );
};

export default EmailLinkForm;
